import classNames from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import InlineSpinner from '../../../common/components/misc/InlineSpinner';
import * as Actions from '../../../common/services/customer/Actions';
import * as CustomerService from '../../../common/services/customer/Service';
import { CustomerError } from '../../../common/services/customer/Types';
import { CustomerDataDTO } from '../../../common/types/dto/CustomerDataDTO';
import { LastVisitsChartDTO } from '../../../common/types/dto/LastVisitsChartDTO';
import { VehicleHistoryDTO } from '../../../common/types/dto/VehicleHistoryDTO';
import checkedImg from '../../assets/images/icons/Cinzentos/checked.jpg';
import { RootState } from '../../base/RootReducer';
import { config } from '../../common/';
import AlertMessage from '../../common/Components/AlertMessage';
import PrimeCollapsible from '../../common/Components/PrimeCollapsible';
import { AppThunkDispatch } from '../../common/types';

interface VehicleVisitsState {
    invoices: VehicleHistoryDTO | undefined;
    nifErrorText: string;
    nif: string;
    history: boolean;
    vehcode: number;
    showLoading: boolean;
}

interface VehicleVisitsProps {
    vehcode: number;
}

interface ReduxStateProps {
    isCommunicating: boolean;
    isCommunicatingUpdateNIF: boolean;
    customerData: CustomerDataDTO | undefined;
    vehicleAllVisits: VehicleHistoryDTO;
}

interface ReduxDispatchProps {
    customerActions: typeof Actions;
    customerServiceActions: CustomerService.ServiceActions;
    dispatch: AppThunkDispatch;
}

type VehicleVisitsProperties =
    ReduxStateProps
    & ReduxDispatchProps
    & VehicleVisitsProps;

const VehicleVisitsContainer: React.FC<VehicleVisitsProperties> = (Props) => {
    const initialState: VehicleVisitsState = {
        invoices: undefined,
        nifErrorText: '',
        nif: '',
        history: true,
        vehcode: -1,
        showLoading: false,
    };
    const [state, setState]: [VehicleVisitsState, React.Dispatch<any>] = useReducer(
        (oldState, newState) => ({ ...oldState, ...newState }), initialState,
    );
    const { t } = useTranslation();

    useEffect(() => {
        if (!Props.customerData || !Props.customerData.nif || Props.customerData.nif === '') {
            return;
        } else if ((!state.invoices && state.invoices !== null && (!Props.vehcode || Props.vehcode === -1))) {
            setState({ invoices: null });
        } else {
            if (Props.vehcode === state.vehcode) {
                return;
            }
            setState({
                vehcode: Props.vehcode,
                invoices: null,
                showLoading: true,
            });
            Props.customerServiceActions.vehicleAllVisits(Props.vehcode);
        }
    }, [state.invoices, state.vehcode, Props.vehcode]);

    useEffect(() => {
        if (Props.vehicleAllVisits && Props.vehicleAllVisits.requestedVehcode && Props.vehicleAllVisits.requestedVehcode === state.vehcode) {
            setState({
                invoices: Props.vehicleAllVisits,
                showLoading: false,
            });
        }
    }, [Props.vehicleAllVisits]);

    function onNifChange(event: React.FormEvent<HTMLInputElement>) {
        setState({
            nif: event.currentTarget.value,
            nifErrorText: '',
        });
    }

    async function onNIFSubmit(e: React.MouseEvent<{}> | null) {
        if (e) {
            e.preventDefault();
        }
        if (state.nif === '') {
            setState({ nifErrorText: t('common:validationMessages.required') });
            return;
        }
        if (state.nif !== '' && config.Constants.Regex.HtmlInjectionValidation.test(state.nif)) {
            setState({ nifErrorText: t('signup:errors:htmlInjectionValidationRegex') });
            return;
        }

        try {
            await Props.customerServiceActions.updateNif(state.nif);
            Props.dispatch(Notifications.success({
                title: t('success'),
                message: t('profile:updateCustomer:success'),
                position: 'tc',
                autoDismiss: 5,
            }));
            Props.customerServiceActions.customerData();
        } catch (error) {
            if (error instanceof CustomerError) {
                const exceptionType: string = error.customerErrorInfo.exceptionType || 'UnknownException';
                const exceptionErrorMsg: string = t(`exception:${exceptionType.split('.').pop()}`);
                setState({ nifErrorText: `${exceptionErrorMsg}` });
                Props.dispatch(Notifications.error({
                    title: t('error'),
                    message: `${exceptionErrorMsg}`,
                    position: 'tc',
                    autoDismiss: 5,
                }));
            } else {
                throw error;
            }
        }
    }

    const buildVehicleVisits = () => {
        const table: JSX.Element[] = [];
        let content = null;
        if (state.invoices && state.invoices.visits) {
            const invoicesLength: number = state.invoices.visits.length;
            const checkedTd = (
                <td align="center" style={{ verticalAlign: 'middle' }} className={'pacote-manutencao-tabela-img'}>
                    <img src={checkedImg} />
                </td>);
            const emptyTd = (<td />);
            state.invoices.visits.forEach((data: LastVisitsChartDTO, index: number) => {
                content = (
                    <div className={'order'}>
                        <PrimeCollapsible
                            key={index}
                            open={true}
                            trigger={<label className={classNames(['form-label'])}>{data.adjustedOrderDate ? data.adjustedOrderDate : (invoicesLength - index) + 'º visita'}</label>}
                        >
                            <div className={classNames(['columns', 'medium-12'])}>
                                <div className={'flex-container'}>
                                    <p className={classNames(['sem-margens'])}>Data do serviço:</p>
                                    <p className={classNames(['sem-margens', 'padding-left-1', 'text-bold'])}>{data.adjustedOrderDate ? data.adjustedOrderDate : ''}</p>
                                </div>
                                <div className={'flex-container'}>
                                    <p className={classNames(['sem-margens'])}>Nº Ordem:</p>
                                    <p className={classNames(['sem-margens', 'padding-left-1', 'text-bold'])}>{data.orderNumber ? data.orderNumber : ''}</p>
                                </div>
                                <div className={'flex-container'}>
                                    <p className={classNames(['sem-margens'])}>Kms:</p>
                                    <p className={classNames(['sem-margens', 'padding-left-1', 'text-bold'])}>{data.kilometer}</p>
                                </div>
                                <div className={'flex-container'}>
                                    <p className={classNames(['sem-margens'])}>Idade na visita (anos):</p>
                                    <p className={classNames(['sem-margens', 'padding-left-1', 'text-bold'])}>{data.ageVehicle >= 0 ? data.ageVehicle : ''}</p>
                                </div>
                                <div className={'flex-container'}>
                                    <p className={classNames(['sem-margens'])}>Meses desde a última visita:</p>
                                    <p className={classNames(['sem-margens', 'padding-left-1', 'text-bold'])}>{data.lastVisitMonths}</p>
                                </div>
                            </div>
                            <div className={classNames(['columns', 'medium-12'])}>
                                <hr />
                            </div>
                            <div className={classNames(['columns', 'medium-12'])}>
                                <div className={'flex-container'}>
                                    <p className={classNames(['sem-margens'])}>Ponto de Serviço autorizado:</p>
                                    <p className={classNames(['sem-margens', 'padding-left-1', 'text-bold'])}>{data.dealerName ? data.dealerName : ''}</p>
                                </div>
                            </div>
                            <div className={classNames(['columns', 'medium-12'])}>
                                <hr />
                            </div>
                            <div className={classNames(['columns', 'medium-12'])}>
                                <p className={'text-bold'}>Tarefas realizadas</p>
                                <div className={'flex-container'}>
                                    <p className={classNames(['sem-margens'])}>Diagnóstico:</p>
                                    <p className={classNames(['sem-margens', 'padding-left-1', 'text-bold'])}>{data.tasks ? data.tasks : ''}</p>
                                </div>
                                <div className={classNames(['tasks'])}>
                                    <div className={classNames(['checked-list', 'checked-list-margin-x'])}>
                                        <table>
                                            <tbody>
                                                <tr className={classNames({ checked: data.maintenanceFlag === 1 })}>
                                                    {data.maintenanceFlag === 1 ? checkedTd : emptyTd}
                                                    <td align="center" style={{ verticalAlign: 'middle' }}>Manutenção</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className={classNames(['checked-list', 'checked-list-margin-x'])}>
                                        <table>
                                            <tbody>
                                                <tr className={classNames({ checked: data.oilFlag === 1 })}>
                                                    {data.oilFlag === 1 ? checkedTd : emptyTd}
                                                    <td align="center" style={{ verticalAlign: 'middle' }}>Óleo</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className={classNames(['checked-list', 'checked-list-margin-x'])}>
                                        <table>
                                            <tbody>
                                                <tr className={classNames({ checked: data.brakesFlag === 1 })}>
                                                    {data.brakesFlag === 1 ? checkedTd : emptyTd}
                                                    <td align="center" style={{ verticalAlign: 'middle' }}>Travões</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className={classNames(['checked-list', 'checked-list-margin-x'])}>
                                        <table>
                                            <tbody>
                                                <tr className={classNames({ checked: data.tyresFlag === 1 })}>
                                                    {data.tyresFlag === 1 ? checkedTd : emptyTd}
                                                    <td align="center" style={{ verticalAlign: 'middle' }}>Pneus</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className={classNames(['checked-list', 'checked-list-margin-x'])}>
                                        <table>
                                            <tbody>
                                                <tr className={classNames({ checked: data.warrantyFlag === 1 })}>
                                                    {data.warrantyFlag === 1 ? checkedTd : emptyTd}
                                                    <td align="center" style={{ verticalAlign: 'middle' }}>Garantia</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </PrimeCollapsible>
                    </div>
                );
                table.push(content);
            });
        }
        return table.length > 0 ? table : (<AlertMessage msg={t('contractsinvoices:invoice.empty')} type={'secondary'} />);
    };

    const BuildNIFForm: JSX.Element = (Props.customerData && (!Props.customerData.nif || Props.customerData.nif === '')) ? (
        <React.Fragment>
            <div className={classNames(['columns', 'medium-12'])}>
                <hr />
            </div>
            <div className={classNames(['columns', 'medium-12'])}>
                <div className={classNames(['medium-4'])}>
                    <label className={classNames(['form-label', 'form-label-margin'])}>{t('signup:formFields.nif')}</label>
                    <input type="text" maxLength={config.Validations.Nif.MaxLength} onChange={onNifChange} />
                    <div className={'error-message'}>{state.nifErrorText}</div>
                </div>
                <div className={classNames(['small', 'button-group', 'margin-bottom-0'])}>
                    <button type="button" className={classNames(['button', 'button-confirm'])} onClick={onNIFSubmit} disabled={Props.isCommunicatingUpdateNIF}>
                        {Props.isCommunicatingUpdateNIF ? t('loadingText') : t('change')}
                    </button>
                </div>
            </div>
        </React.Fragment>) : <React.Fragment />;

    return (
        <React.Fragment>
            <div className={classNames(['columns', 'medium-12'])}>
                <p className={'text-bold'}>{t('contractsinvoices:invoice.header')}</p>
                <label>{t('contractsinvoices:invoice.note')}</label>
                <p className={classNames(['sem-margens', 'margin-top-1', 'text-bold'])}>
                    {!Props.customerData ? '' : (!Props.customerData.nif || Props.customerData.nif === '') ? t('contractsinvoices:invoice.noNifMessage') : t('contractsinvoices:invoice.yesNifMessage')}
                </p>
            </div>
            {BuildNIFForm}
            <div className={classNames(['columns', 'medium-12'])}>
                <hr />
            </div>
            <div className={classNames(['columns', 'medium-12'])}>
                {state.invoices && state.invoices.visits && state.vehcode && state.vehcode !== -1 && buildVehicleVisits()}
                {(Props.isCommunicating || state.showLoading) && <InlineSpinner size={'100px'} />}
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = (store: RootState) => ({
    isCommunicating: store.customerState.vehicleAllVisits.isCommunicating,
    isCommunicatingUpdateNIF: store.customerState.updateNif.isCommunicating,
    vehicleAllVisits: store.customerState.vehicleAllVisits.data,
    customerData: store.customerState.customerData.data.customerData,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    customerActions: bindActionCreators(Actions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(VehicleVisitsContainer);
